"use client";

import React, { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Send, CircleCheck } from "lucide-react";
export default function ContributorApplication() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224174" id="kindly-respond" ref={ref} className={`w-full py-24 bg-background transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground tracking-tight italic">
            Kindly Respond
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            We look forward to celebrating our love with you. Please let us know if you can join us by June 1st.
          </p>
        </div>

        <div className="bg-card border-none shadow-xl rounded-md p-6 md:p-10 relative overflow-hidden">
          <FormProvider formKey="rsvp_form_rsvp" formId="rsvp_form_rsvp" sectionName="Contributor Application" defaultValues={{
          full_name: "",
          email: "",
          short_bio: "",
          sample_link: "",
          interest_areas: [],
          agree_terms: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          short_bio: {
            required: true,
            maxLength: 150
          },
          sample_link: {
            required: true,
            pattern: "url"
          },
          interest_areas: {
            required: true
          },
          agree_terms: {
            required: true
          }
        }} submitText="Send RSVP" successMessage="Thank you for your response! We can't wait to see you.">
            <ApplicationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ApplicationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const progressValue = step / totalSteps * 100;
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("RSVP Sent", {
        description: successMessage
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(2);
    window.scrollTo({
      top: document.getElementById("kindly-respond")?.offsetTop,
      behavior: "smooth"
    });
  };
  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center text-secondary">
          <CircleCheck className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-foreground">Response Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto font-default">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 font-sans tracking-widest text-xs uppercase border-primary/20">
          Update Response
        </Button>
      </div>;
  }
  return <>
      <Toaster position="top-center" />
      
      <div className="mb-8 space-y-4">
        <div className="flex justify-between text-[10px] font-sans uppercase tracking-widest text-muted-foreground">
          <span className={step >= 1 ? "text-foreground" : ""}>Guest Details</span>
          <span className={step >= 2 ? "text-foreground" : ""}>Attendance & Preferences</span>
        </div>
        <Progress value={progressValue} className="h-1 bg-muted" />
      </div>

      <Form onSubmit={handleSubmit} className="space-y-8">
        {}
        <div className={step === 1 ? "space-y-6 animate-in slide-in-from-left-4 fade-in duration-300" : "hidden"}>
          <FormField control={control} name="full_name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Jane & Liam" className="bg-transparent border-0 border-b border-input px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors font-default text-lg" {...field} />
                </FormControl>
                <FormDescription className="font-default italic text-xs">Please enter the names of all guests in your party.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="hello@vow.com" className="bg-transparent border-0 border-b border-input px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors font-default text-lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="short_bio" rules={{
          required: "A message is required",
          maxLength: {
            value: 150,
            message: "Maximum 150 characters allowed"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">A Note for the Couple</FormLabel>
                <FormControl>
                  <Textarea placeholder="Share a warm wish or a memory..." className="bg-transparent border-0 border-b border-input px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors font-default text-lg resize-none h-24" {...field} />
                </FormControl>
                <div className="flex justify-between items-center">
                  <FormMessage />
                  <span className="text-[10px] font-sans tracking-widest text-muted-foreground ml-auto">
                    {field.value?.length || 0}/150
                  </span>
                </div>
              </FormItem>} />

          <div className="pt-4 flex justify-end">
            <Button type="button" onClick={handleNext} className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-sans uppercase tracking-widest text-xs h-12 px-10">
              Next Details
              <ArrowRight className="ml-2 size-3" />
            </Button>
          </div>
        </div>

        {}
        <div className={step === 2 ? "space-y-6 animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
          <FormField control={control} name="sample_link" rules={{
          required: "Dietary requirements are helpful for our planning"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">Dietary Requirements</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="e.g. Vegetarian, Nut Allergy" className="bg-transparent border-0 border-b border-input px-0 focus-visible:ring-0 focus-visible:border-primary transition-colors font-default text-lg" {...field} />
                </FormControl>
                <FormDescription className="font-default italic text-xs">Let us know if you have any specific meal preferences or allergies.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="interest_areas" rules={{
          required: "Please select your attendance status"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">Will you attend?</FormLabel>
                <FormDescription className="mb-3 font-default italic text-xs">Select your attendance status for the ceremony and reception.</FormDescription>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 p-4 border border-border rounded-md bg-background/30">
                  {["Joyfully Accept", "Regretfully Decline"].map((option, i) => <label key={i} data-index={i} className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative flex items-center justify-center">
                        <input type="checkbox" className="peer appearance-none size-5 border border-input rounded-sm checked:bg-primary checked:border-primary transition-colors cursor-pointer" value={option} checked={field.value?.includes(option) || false} onChange={e => {
                  const current = field.value || [];
                  const updated = e.target.checked ? [option] : [];
                  field.onChange(updated);
                }} />
                        <CircleCheck className="absolute size-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3} />
                      </div>
                      <span className="text-sm font-default font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 group-hover:text-primary transition-colors">
                        {option}
                      </span>
                    </label>)}
                </div>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agree_terms" rules={{
          required: "Please confirm your response"
        }} render={({
          field
        }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4 bg-muted/10 mt-6">
                <FormControl>
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input type="checkbox" className="peer appearance-none size-5 border border-input rounded-sm checked:bg-primary checked:border-primary transition-colors cursor-pointer" checked={field.value || false} onChange={field.onChange} />
                    <CircleCheck className="absolute size-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3} />
                  </div>
                </FormControl>
                <div className="space-y-1.5 leading-none">
                  <FormLabel className="cursor-pointer font-default text-sm font-medium">
                    I confirm this response is final for my party
                  </FormLabel>
                  <FormDescription className="text-[10px] font-sans uppercase tracking-widest">
                    Required to submit RSVP.
                  </FormDescription>
                </div>
              </FormItem>} />

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-xs font-sans uppercase tracking-widest flex items-start">
              <span className="mr-2"></span>
              <p>{submitError} Please check all fields before sending.</p>
            </div>}

          <div className="pt-6 flex flex-col sm:flex-row gap-4 justify-between items-center border-t border-border">
            <Button type="button" variant="ghost" onClick={handleBack} className="w-full sm:w-auto order-2 sm:order-1 font-sans uppercase tracking-widest text-[10px] hover:bg-secondary/50">
              <ArrowLeft className="mr-2 size-3" />
              Back to Info
            </Button>
            
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto order-1 sm:order-2 bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 px-12 h-12 font-sans uppercase tracking-widest text-xs shadow-md rounded-md">
              {isSubmitting ? <span className="flex items-center">
                  <span className="animate-spin mr-2 size-3 border-2 border-current border-t-transparent rounded-lg" />
                  Sending...
                </span> : <span className="flex items-center">
                  Send RSVP
                  <Send className="ml-2 size-3" />
                </span>}
            </Button>
          </div>
        </div>
      </Form>
    </>;
}